import { motion } from "framer-motion";
import { chapters, profile, aboutBio, aboutPhotos } from "@/data/portfolio";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const About = () => {
  return (
    <section id="about" className="relative py-24 lg:py-36" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={0}
              className="lg:sticky lg:top-32"
            >
              <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
                {"// 01 — Manifesto"}
              </span>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                Numbers are my<br />
                <span className="gradient-text">native language</span>
              </h2>

              <p
                className="mt-6 text-sm leading-relaxed text-slate-400 sm:text-base"
                data-testid="about-bio"
              >
                {aboutBio}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4" data-testid="about-photos">
                {aboutPhotos.map((photo, i) => (
                  <div key={photo.src} className="group relative">
                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-br from-cyan-500/25 via-transparent to-violet-600/25 opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-100" />
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className={`relative aspect-[3/4] w-full rounded-xl border border-white/10 object-cover transition-all duration-700 group-hover:scale-[1.03] ${
                        i === 1 ? "mt-6" : ""
                      }`}
                      data-testid={`about-photo-${i}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2" data-testid="about-chips">
                {["Data Science", "Quant Finance", "ML Engineering", "Photography"].map((chip) => (
                  <span
                    key={chip}
                    className="glass rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="space-y-14 lg:col-span-7">
            {chapters.map((ch, i) => (
              <motion.article
                key={ch.number}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                custom={i + 1}
                className="group relative border-l border-white/10 pl-8 transition-colors duration-500 hover:border-violet-500/60 sm:pl-12"
                data-testid={`about-chapter-${ch.number}`}
              >
                <span className="font-display text-6xl font-extrabold text-outline transition-all duration-500 group-hover:text-outline-accent sm:text-7xl">
                  {ch.number}
                </span>
                <h3 className="mt-3 font-display text-xl font-semibold text-slate-100 sm:text-2xl">
                  {ch.title}
                </h3>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
                  {ch.body}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
